import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Grid,
  Tabs,
  Tab,
  Typography,
  Chip,
  Pagination,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import ArticleIcon from "@mui/icons-material/Article";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import CloseIcon from "@mui/icons-material/Close";
import InsertChartOutlinedIcon from "@mui/icons-material/InsertChartOutlined";
import { PageHeader, Card, StatsCard, ActionButton } from "../shared";
import ApiServices from "../../config/ApiServices";
import ApiEndpoint from "../../config/ApiEndpoint";

const PAGE_LIMIT = 12;

const PERIOD_TO_DAYS = { "7d": 7, "30d": 30 };

const IMPRESSIONS_VIEWS_EXPLAINER = (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      flexDirection: { xs: "column", sm: "row" },
      flexWrap: "wrap",
      gap: { xs: 0.25, sm: 0 },
    }}
  >
    <Box component="span" sx={{ display: { xs: "block", sm: "inline" } }}>
      <Box component="span" sx={{ fontWeight: 600, fontStyle: "normal" }}>Impressions</Box>
      {" = How many times posts appear in member feeds."}
    </Box>
    <Box component="span" sx={{ display: { xs: "block", sm: "inline" }, ml: { xs: 0, sm: 1.25 } }}>
      <Box component="span" sx={{ fontWeight: 600, fontStyle: "normal" }}>Views</Box>
      {" = How many times posts are opened and read."}
    </Box>
  </Box>
);

const EMPTY_ANALYTICS = {
  total_impressions: 0,
  total_views: 0,
  total_likes: 0,
  total_saves: 0,
  impressions_by_day: [],
  views_by_day: [],
  top_content: [],
  device_breakdown: {},
};

const EMPTY_POST_ANALYTICS = {
  metrics: {
    total_impressions: 0,
    total_views: 0,
    total_likes: 0,
    total_saves: 0,
    engagement_rate: 0,
  },
  charts: {
    impressions_by_day: [],
    views_by_day: [],
    likes_by_day: [],
  },
};

const ClubContent = (props) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const location = useLocation();
  const { id: subOrgId } = useParams();
  const token = props?.props?.profile?.token;
  const userType =
    props?.props?.profile?.user_type || props?.props?.profile?.data?.user_type;

  const [clubName, setClubName] = useState(location.state?.clubName || "");
  const [tab, setTab] = useState(location.state?.initialTab ?? 0);

  // Content tab state
  const [content, setContent] = useState([]);
  const [contentLoading, setContentLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Analytics tab state
  const [analytics, setAnalytics] = useState(EMPTY_ANALYTICS);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [period, setPeriod] = useState("30d");

  // Per-post analytics dialog
  const [postDialog, setPostDialog] = useState({
    open: false,
    loading: false,
    data: null,
    title: "",
  });

  const headers = useCallback(
    () => ({ "Content-Type": "application/json", "x-access-token": token }),
    [token]
  );

  const axisColor = isDark ? "#cbd5e1" : "#718096";
  const tooltipStyle = {
    backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
    border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid #e2e8f0",
    borderRadius: "8px",
    color: isDark ? "#ffffff" : "#1a202c",
  };
  const tableHeadSx = { backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "#f8fafc" };
  const cellSx = {
    borderColor: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
    fontSize: "0.875rem",
    color: isDark ? "#e2e8f0" : "#334155",
  };
  const headCellSx = {
    ...cellSx,
    fontWeight: 600,
    fontSize: "0.75rem",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: isDark ? "#94a3b8" : "#64748b",
  };
  const mutedTextSx = { color: isDark ? "#64748b" : "#94a3b8", fontSize: "0.875rem" };

  // Resolve club name if we navigated here without it (e.g. direct link / refresh).
  useEffect(() => {
    if (!token || !subOrgId || clubName) return;
    (async () => {
      const data = await ApiServices.GetApiCall(
        `${ApiEndpoint.MASTER_ORG_SUB_ORG_DETAILS}${subOrgId}`,
        headers()
      );
      if (data?.status && data.data?.name) setClubName(data.data.name);
    })();
  }, [token, subOrgId, clubName, headers]);

  const fetchContent = useCallback(async () => {
    if (!token || !subOrgId) return;
    setContentLoading(true);
    const url = `${ApiEndpoint.MASTER_ORG_SUB_ORG_CONTENT(subOrgId)}?page=${page}&limit=${PAGE_LIMIT}`;
    const data = await ApiServices.GetApiCall(url, headers());
    setContentLoading(false);
    if (data?.status) {
      setContent(data.data || []);
      setTotalPages(data.total_pages || 1);
      setTotalItems(data.total_items || 0);
    } else {
      setContent([]);
      setTotalPages(1);
      setTotalItems(0);
    }
  }, [token, subOrgId, page, headers]);

  const fetchAnalytics = useCallback(async () => {
    if (!token || !subOrgId) return;
    setAnalyticsLoading(true);
    const url = `${ApiEndpoint.MASTER_ORG_SUB_ORG_CONTENT_ANALYTICS(subOrgId)}?days=${PERIOD_TO_DAYS[period]}`;
    const data = await ApiServices.GetApiCall(url, headers());
    setAnalyticsLoading(false);
    if (data?.status && data.data) {
      setAnalytics(data.data);
    } else {
      setAnalytics(EMPTY_ANALYTICS);
    }
  }, [token, subOrgId, period, headers]);

  useEffect(() => {
    if (userType === "master_org") fetchContent();
  }, [userType, fetchContent]);

  useEffect(() => {
    if (userType === "master_org" && tab === 1) {
      fetchAnalytics();
      fetchContent();
    }
  }, [userType, tab, fetchAnalytics, fetchContent]);

  const openPostAnalytics = async (post) => {
    setPostDialog({ open: true, loading: true, data: null, title: post.title || "Post" });
    const url = `${ApiEndpoint.MASTER_ORG_SUB_ORG_CONTENT_ITEM_ANALYTICS(subOrgId, post.id)}?days=${PERIOD_TO_DAYS[period]}`;
    const data = await ApiServices.GetApiCall(url, headers());
    if (data?.status) {
      setPostDialog((prev) => ({ ...prev, loading: false, data: data.data }));
    } else {
      setPostDialog((prev) => ({
        ...prev,
        loading: false,
        data: EMPTY_POST_ANALYTICS,
      }));
    }
  };

  const closePostDialog = () =>
    setPostDialog({ open: false, loading: false, data: null, title: "" });

  if (userType !== "master_org") {
    return (
      <Box>
        <PageHeader title="Club Content" subtitle="Access denied." />
        <Card>
          <Typography sx={{ color: isDark ? "#cbd5e1" : "#718096" }}>
            This page is for club partner accounts only.
          </Typography>
        </Card>
      </Box>
    );
  }

  const renderEmpty = (msg) => (
    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
      <Typography sx={mutedTextSx}>{msg}</Typography>
    </Box>
  );

  // Daily trend data merged for club-level impressions & views chart.
  const clubTrend = (() => {
    const map = new Map();
    (analytics.impressions_by_day || []).forEach((d) =>
      map.set(d.date, { date: d.date, impressions: d.count, views: 0 })
    );
    (analytics.views_by_day || []).forEach((d) => {
      const row = map.get(d.date) || { date: d.date, impressions: 0, views: 0 };
      row.views = d.count;
      map.set(d.date, row);
    });
    return Array.from(map.values()).sort((a, b) => (a.date < b.date ? -1 : 1));
  })();

  const impressionsViewsHeaderSx = {
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    flexWrap: "wrap",
    alignItems: { xs: "flex-start", sm: "baseline" },
    gap: { xs: 0.5, sm: 1.25 },
    mb: 2,
  };

  const renderImpressionsViewsHeader = () => (
    <Box sx={impressionsViewsHeaderSx}>
      <Typography
        component="span"
        sx={{
          fontSize: "1.125rem",
          fontWeight: 600,
          color: isDark ? "#ffffff" : "#1a202c",
          flexShrink: 0,
        }}
      >
        Impressions & Views
      </Typography>
      <Typography
        component="span"
        sx={{
          fontSize: "0.75rem",
          lineHeight: 1.5,
          fontStyle: "italic",
          color: isDark ? "#94a3b8" : "#64748b",
          width: { xs: "100%", sm: "auto" },
        }}
      >
        {IMPRESSIONS_VIEWS_EXPLAINER}
      </Typography>
    </Box>
  );

  const periodToggle = (
    <ToggleButtonGroup
      value={period}
      exclusive
      onChange={(_, val) => { if (val) setPeriod(val); }}
      size="small"
      sx={{
        "& .MuiToggleButton-root": {
          px: 3,
          borderColor: isDark ? "rgba(255,255,255,0.15)" : "#e2e8f0",
          color: isDark ? "#94a3b8" : "#64748b",
          "&.Mui-selected": {
            backgroundColor: isDark ? "rgba(99,102,241,0.2)" : "rgba(99,102,241,0.1)",
            color: isDark ? "#a5b4fc" : "#6366f1",
            borderColor: "#6366f1",
          },
        },
      }}
    >
      <ToggleButton value="7d">Last 7 days</ToggleButton>
      <ToggleButton value="30d">Last 30 days</ToggleButton>
    </ToggleButtonGroup>
  );

  return (
    <Box>
      <PageHeader
        title={clubName ? `${clubName} — Content` : "Club Content"}
        subtitle="View this club's published content and its engagement analytics."
        breadcrumbs={[
          { label: "Club Partner" },
          { label: "Clubs", onClick: () => navigate("/master-org/sub-orgs") },
          { label: "Content" },
        ]}
      >
        <ActionButton
          variant="outline"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/master-org/sub-orgs")}
        >
          Back
        </ActionButton>
      </PageHeader>

      <Tabs
        value={tab}
        onChange={(_, val) => setTab(val)}
        sx={{
          mb: 3,
          "& .MuiTab-root": { textTransform: "none", fontWeight: 600 },
          "& .Mui-selected": { color: isDark ? "#a5b4fc !important" : "#6366f1 !important" },
          "& .MuiTabs-indicator": { backgroundColor: "#6366f1" },
        }}
      >
        <Tab label="Content" />
        <Tab label="Analytics" />
      </Tabs>

      {/* CONTENT TAB */}
      {tab === 0 && (
        <Box>
          {contentLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress size={36} />
            </Box>
          ) : content.length === 0 ? (
            <Card>{renderEmpty("This club has not published any content yet.")}</Card>
          ) : (
            <>
              <Typography sx={{ ...mutedTextSx, mb: 2 }}>
                {totalItems} {totalItems === 1 ? "post" : "posts"}
              </Typography>
              <Grid container spacing={2}>
                {content.map((post) => (
                  <Grid item xs={12} sm={6} md={4} key={post.id}>
                    <Card noPadding sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                      {post.image_url ? (
                        <Box
                          component="img"
                          src={post.image_url}
                          alt={post.title || "content"}
                          sx={{
                            width: "100%",
                            aspectRatio: "16 / 9",
                            objectFit: "cover",
                            borderTopLeftRadius: "12px",
                            borderTopRightRadius: "12px",
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: "100%",
                            aspectRatio: "16 / 9",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "#f1f5f9",
                            borderTopLeftRadius: "12px",
                            borderTopRightRadius: "12px",
                          }}
                        >
                          <ArticleIcon sx={{ fontSize: 40, color: isDark ? "#475569" : "#cbd5e1" }} />
                        </Box>
                      )}
                      <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1, flex: 1 }}>
                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                          {post.sport?.name && (
                            <Chip
                              label={post.sport.name}
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: "0.7rem",
                                fontWeight: 600,
                                backgroundColor: isDark ? "rgba(34,197,94,0.2)" : "rgba(34,197,94,0.15)",
                                color: isDark ? "#4ade80" : "#22c55e",
                              }}
                            />
                          )}
                        </Box>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.95rem",
                            color: isDark ? "#ffffff" : "#1a202c",
                          }}
                        >
                          {post.title || "Untitled"}
                        </Typography>
                        {post.content && (
                          <Typography
                            sx={{
                              fontSize: "0.8rem",
                              color: isDark ? "#94a3b8" : "#64748b",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {post.content}
                          </Typography>
                        )}
                        <Box
                          sx={{
                            mt: "auto",
                            pt: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Box sx={{ display: "flex", gap: 2 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <VisibilityIcon sx={{ fontSize: 16, color: axisColor }} />
                              <Typography sx={{ fontSize: "0.8rem", color: axisColor }}>
                                {(post.view_count || 0).toLocaleString()}
                              </Typography>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <ThumbUpAltIcon sx={{ fontSize: 15, color: axisColor }} />
                              <Typography sx={{ fontSize: "0.8rem", color: axisColor }}>
                                {(post.like_count || 0).toLocaleString()}
                              </Typography>
                            </Box>
                          </Box>
                          <ActionButton
                            variant="ghost"
                            size="small"
                            startIcon={<InsertChartOutlinedIcon />}
                            onClick={() => openPostAnalytics(post)}
                            sx={{ minWidth: "auto" }}
                          >
                            Analytics
                          </ActionButton>
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              {totalPages > 1 && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, val) => setPage(val)}
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      )}

      {/* ANALYTICS TAB */}
      {tab === 1 && (
        <Box>
          <Box sx={{ mb: 3 }}>{periodToggle}</Box>

          {analyticsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress size={36} />
            </Box>
          ) : (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={4} md={2}>
                  <StatsCard
                    title="Posts"
                    value={totalItems.toLocaleString()}
                    subtitle="Published posts"
                    icon={ArticleIcon}
                    color="primary"
                  />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <StatsCard title="Views" value={(analytics.total_views || 0).toLocaleString()} icon={VisibilityIcon} color="primary" />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <StatsCard title="Likes" value={(analytics.total_likes || 0).toLocaleString()} icon={ThumbUpAltIcon} color="success" />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <StatsCard title="Saves" value={(analytics.total_saves || 0).toLocaleString()} icon={BookmarkIcon} color="info" />
                </Grid>
              </Grid>

              <Card sx={{ mb: 3 }}>
                {renderImpressionsViewsHeader()}
                {clubTrend.length > 0 ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={clubTrend} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0"} />
                      <XAxis dataKey="date" stroke={axisColor} tick={{ fill: axisColor, fontSize: "0.72rem" }} />
                      <YAxis stroke={axisColor} tick={{ fill: axisColor, fontSize: "0.72rem" }} />
                      <Tooltip contentStyle={tooltipStyle} />
                      <Legend />
                      <Line type="monotone" dataKey="impressions" name="Impressions" stroke="#6366f1" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                      <Line type="monotone" dataKey="views" name="Views" stroke="#22c55e" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  renderEmpty("No trend data for this period.")
                )}
              </Card>

              <Card title="Top Content" sx={{ mb: 3 }}>
                {analytics.top_content?.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead sx={tableHeadSx}>
                        <TableRow>
                          <TableCell sx={headCellSx}>Title</TableCell>
                          <TableCell sx={headCellSx}>Sport</TableCell>
                          <TableCell sx={headCellSx} align="right">Views</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {analytics.top_content.map((row) => (
                          <TableRow key={row.id}>
                            <TableCell sx={cellSx}>{row.title || "Untitled"}</TableCell>
                            <TableCell sx={cellSx}>{row.sport?.name || "-"}</TableCell>
                            <TableCell sx={cellSx} align="right">{(row.view_count || 0).toLocaleString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  renderEmpty("No content engagement for this period.")
                )}
              </Card>

            </>
          )}
        </Box>
      )}

      {/* PER-POST ANALYTICS DIALOG */}
      <Dialog
        open={postDialog.open}
        onClose={closePostDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
            borderRadius: "16px",
            margin: { xs: 2, sm: 3 },
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 2,
            px: { xs: 2, sm: 3 },
            borderBottom: isDark ? "1px solid rgba(255,255,255,0.05)" : "1px solid #e2e8f0",
          }}
        >
          <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: isDark ? "#ffffff" : "#1a202c", pr: 2 }}>
            {postDialog.title}
          </Typography>
          <IconButton onClick={closePostDialog} sx={{ color: isDark ? "#cbd5e1" : "#718096" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, px: { xs: 2, sm: 3 } }}>
          {postDialog.loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={32} />
            </Box>
          ) : postDialog.data ? (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={4}>
                  <StatsCard title="Views" value={(postDialog.data.metrics?.total_views || 0).toLocaleString()} icon={VisibilityIcon} color="primary" />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <StatsCard title="Likes" value={(postDialog.data.metrics?.total_likes || 0).toLocaleString()} icon={ThumbUpAltIcon} color="success" />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <StatsCard title="Saves" value={(postDialog.data.metrics?.total_saves || 0).toLocaleString()} icon={BookmarkIcon} color="info" />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <StatsCard title="Engagement" value={`${postDialog.data.metrics?.engagement_rate || 0}%`} icon={ShowChartIcon} color="primary" />
                </Grid>
              </Grid>

              <Card>
                {renderImpressionsViewsHeader()}
                {(() => {
                  const charts = postDialog.data.charts || {};
                  const map = new Map();
                  (charts.impressions_by_day || []).forEach((d) =>
                    map.set(d.date, { date: d.date, impressions: d.count, views: 0, likes: 0 })
                  );
                  (charts.views_by_day || []).forEach((d) => {
                    const row = map.get(d.date) || { date: d.date, impressions: 0, views: 0, likes: 0 };
                    row.views = d.count;
                    map.set(d.date, row);
                  });
                  (charts.likes_by_day || []).forEach((d) => {
                    const row = map.get(d.date) || { date: d.date, impressions: 0, views: 0, likes: 0 };
                    row.likes = d.count;
                    map.set(d.date, row);
                  });
                  const trend = Array.from(map.values()).sort((a, b) => (a.date < b.date ? -1 : 1));
                  return trend.length > 0 ? (
                    <ResponsiveContainer width="100%" height={240}>
                      <LineChart data={trend} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0"} />
                        <XAxis dataKey="date" stroke={axisColor} tick={{ fill: axisColor, fontSize: "0.72rem" }} />
                        <YAxis stroke={axisColor} tick={{ fill: axisColor, fontSize: "0.72rem" }} />
                        <Tooltip contentStyle={tooltipStyle} />
                        <Legend />
                        <Line type="monotone" dataKey="impressions" name="Impressions" stroke="#6366f1" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                        <Line type="monotone" dataKey="views" name="Views" stroke="#22c55e" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                        <Line type="monotone" dataKey="likes" name="Likes" stroke="#f59e0b" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    renderEmpty("No trend data for this period.")
                  );
                })()}
              </Card>
            </>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: isDark ? "1px solid rgba(255,255,255,0.05)" : "1px solid #e2e8f0" }}>
          <Button onClick={closePostDialog} sx={{ textTransform: "none" }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClubContent;
